import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  NotificationsNone,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AuthenticatedFileImage from 'components/AuthenticatedFileImage';
import NotificationsServices from 'services/NotificationsServices';
import localStorageService from 'services/localStorageService';

const getUnreadCountValue = (payload) => {
  const rawValue =
    payload?.unread_count ??
    payload?.data?.unread_count ??
    payload?.view?.unread_count ??
    0;

  const parsedValue = Number(rawValue);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const IGNORED_KEYCLOAK_ROLES = ['offline_access', 'default-roles-codex', 'uma_authorization'];
const normalizeRole = (role = '') => String(role).trim().replace(/\s+/g, ' ').toLowerCase();

const getSelectableRoles = (roles = []) => {
  const ignoredRoleSet = new Set(IGNORED_KEYCLOAK_ROLES.map(normalizeRole));
  const uniqueRoleMap = new Map();

  roles.forEach((role) => {
    const roleName = String(role || '').trim();
    if (!roleName) return;

    const normalizedRole = normalizeRole(roleName);
    if (ignoredRoleSet.has(normalizedRole)) return;

    if (!uniqueRoleMap.has(normalizedRole)) {
      uniqueRoleMap.set(normalizedRole, roleName);
    }
  });

  return Array.from(uniqueRoleMap.values());
};

const Header = ({ onMenuClick, sidebarWidth = 240, setCollapsed }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector(state => state.theme);
  const authState = useSelector(state => state.auth);
  const user = authState.user;
  const availableRoles = Array.isArray(authState.availableRoles) && authState.availableRoles.length > 0
    ? authState.availableRoles
    : [];
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [pendingRole, setPendingRole] = React.useState('');
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const activeUser = user?.user || user || {};
  const activeAuthUser = user || {};
  const currentRole = authState.selectedRole || (Array.isArray(activeAuthUser?.roles) ? activeAuthUser.roles[0] : null);
  const resolvedAvailableRoles = availableRoles.length > 0
    ? availableRoles
    : getSelectableRoles(activeAuthUser?.roles || []);
  const profilePicName =
    activeUser?.profile_picture_name ||
    activeUser?.profile_pic_name ||
    null;
  const profilePicPath =
    activeUser?.profile_picture_path ||
    activeUser?.profile_pic_path ||
    null;
  const displayInitial = activeUser?.name || activeUser?.full_name
    ? (activeUser?.name || activeUser?.full_name)[0].toUpperCase()
    : 'U';

  const toggleTheme = () => {
    dispatch({ type: 'TOGGLE_THEME' });
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };
  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    localStorageService.removeItem('all_user_roles');
    localStorageService.removeItem('selected_user_role');
    handleMenuClose();
    navigate('/login');
  };

  const handleSwitchRole = (roleName) => {
    dispatch({ type: 'UPDATE_USER', payload: { roles: [roleName] } });
    dispatch({ type: 'SET_AVAILABLE_ROLES', payload: { availableRoles: resolvedAvailableRoles, selectedRole: roleName } });
    localStorageService.setItem('selected_user_role', roleName);
    handleMenuClose();
    navigate('/dashboard');
  };

  const handleOpenRoleDialog = () => {
    const defaultRole = currentRole || resolvedAvailableRoles?.[0] || '';
    setPendingRole(defaultRole);
    setRoleDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
  };

  const handleConfirmRoleSwitch = () => {
    if (!pendingRole) return;
    setRoleDialogOpen(false);
    handleSwitchRole(pendingRole);
  };

  const loadUnreadCount = React.useCallback(async () => {
    const response = await NotificationsServices.getUnreadCount();
    if (response.success) {
      setUnreadCount(getUnreadCountValue(response?.data));
    }
  }, []);

  React.useEffect(() => {
    loadUnreadCount();
    const intervalId = window.setInterval(() => {
      loadUnreadCount();
    }, 120000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadUnreadCount]);

  const handleOpenNotifications = () => {
    navigate('/notifications');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        ml: { md: `${sidebarWidth}px` },
        width: { md: `calc(100% - ${sidebarWidth}px)` },
        transition: 'margin-left 0.3s, width 0.3s',
        top: 0,
        left: 0,
        zIndex: (theme) => theme.zIndex.drawer -1,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 48, md: 50 } }}>{/* top bar height adjust */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => {
            if (isMobile) {
              onMenuClick();
            } else {
              setCollapsed((prev) => !prev);
            }
          }}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>

        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="View notifications">
            <IconButton
              color="inherit"
              onClick={handleOpenNotifications}
              aria-label="view notifications"
            >
              <Badge
                badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                color="error"
                showZero
              >
                <NotificationsNone />
              </Badge>
            </IconButton>
          </Tooltip>
          
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            aria-label="toggle theme"
          >
            {isDarkMode ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
          <IconButton onClick={handleAvatarClick} sx={{ ml: 1 }}>
            <AuthenticatedFileImage fileName={profilePicName} filePath={profilePicPath}>
              {({ imageSrc }) => (
                <Avatar src={imageSrc || undefined} sx={{ width: 32, height: 32 }}>
                  {!imageSrc && displayInitial}
                </Avatar>
              )}
            </AuthenticatedFileImage>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleProfile}>Profile</MenuItem>
              {resolvedAvailableRoles.length > 1 && (
              <>
                <Divider />
                <MenuItem onClick={handleOpenRoleDialog}>Switch Role</MenuItem>
              </>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>

          <Dialog
            open={roleDialogOpen}
            onClose={handleCloseRoleDialog}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Switch Role</DialogTitle>
            <DialogContent dividers>
              <RadioGroup
                value={pendingRole}
                onChange={(event) => setPendingRole(event.target.value)}
              >
                {resolvedAvailableRoles.map((roleName) => (
                  <FormControlLabel
                    key={roleName}
                    value={roleName}
                    control={<Radio />}
                    label={roleName}
                  />
                ))}
              </RadioGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRoleDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleConfirmRoleSwitch}
                disabled={!pendingRole || normalizeRole(pendingRole) === normalizeRole(currentRole || '')}
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
